import { Box, IconButton, SxProps, TextField } from "@outschool/backpack";
import { faPaperPlane } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useMutation } from "@outschool/ui-apollo";
import {
  AttachmentGallery,
  ErrorMessage,
} from "@outschool/ui-components-shared";
import { validOptimisticResponse } from "@outschool/ui-utils";
import React, { useState } from "react";

import type { AttachmentFragmentFragment } from "..";
import { PrivateClassMessageFragment } from "../graphql/fragments/PrivateClassMessage";
import { addPrivateClassMessageMutation } from "../graphql/mutations/PrivateClassMessage";
import InlineEditableAttachments from "./InlineEditableAttachments";

export interface BlockScopedMessageSender {
  uid: string;
  name: string;
  photo: string | null;
  isLearner: boolean;
}

interface BaseBlockScopedMessageInputProps {
  placeholder: string;
  sectionUid: string;
  learnerUid: string;
  sender: BlockScopedMessageSender;
  threadUid?: string;
  onMessageSent?: () => void;
  onThreadCreated?: (threadUid: string) => void;
  sx?: SxProps;
}

export type BlockScopedMessageInputProps = BaseBlockScopedMessageInputProps &
  (
    | { classPostUid: string; learningAssignmentClassPostUid?: never }
    | { classPostUid?: never; learningAssignmentClassPostUid: string }
  );

export default function BlockScopedMessageInput({
  placeholder,
  sectionUid,
  learnerUid,
  sender,
  threadUid,
  classPostUid,
  learningAssignmentClassPostUid,
  onMessageSent,
  onThreadCreated,
  sx,
}: BlockScopedMessageInputProps) {
  const { t } = useTranslation(
    "ui-components-classroom\\BlockScopedMessageInput",
  );
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState<AttachmentFragmentFragment[]>(
    [],
  );
  const [video, setVideo] = useState<AttachmentFragmentFragment | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [addPrivateClassMessage] = useMutation(addPrivateClassMessageMutation);

  const hasContent =
    text.trim().length > 0 || attachments.length > 0 || video !== null;

  const handleDeleteAttachment = (
    attachment: { uid: string },
    _event: React.SyntheticEvent,
  ) => {
    if (video && video.uid === attachment.uid) {
      setVideo(null);
    } else {
      setAttachments(prev => prev.filter(a => a.uid !== attachment.uid));
    }
  };

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!hasContent) {
      return;
    }
    setError(null);
    const sendAttachments = [...attachments];
    const sendVideo = video;
    setText("");
    setAttachments([]);
    setVideo(null);

    try {
      const result = await addPrivateClassMessage({
        variables: {
          privateClassMessageInput: {
            sectionUid,
            learnerUid,
            threadUid,
            classPostUid,
            learningAssignmentClassPostUid,
            messageContentInput: {
              text: trimmed,
              textMimeType: "TEXT_PLAIN",
              attachmentUids: sendAttachments.map(a => a.uid),
              videoUid: sendVideo?.uid,
            },
          },
        },
        optimisticResponse: threadUid
          ? validOptimisticResponse({
              __typename: "Mutation",
              addPrivateClassMessage: {
                __typename: "PrivateClassMessage",
                uid: `temp-${Date.now()}`,
                thread_uid: threadUid,
                sender: {
                  __typename: "MessageSender",
                  uid: sender.uid,
                  name: sender.name,
                  photo: sender.photo,
                  avatar: null,
                  isLearner: sender.isLearner,
                },
                messageContent: {
                  __typename: "MessageContent",
                  uid: `temp-content-${Date.now()}`,
                  text: trimmed,
                  htmlText: trimmed,
                  originalMimeType: "TEXT_PLAIN",
                  attachments: sendAttachments.map(a => ({
                    __typename: "Attachment" as const,
                    ...a,
                  })),
                  video: sendVideo
                    ? { __typename: "Attachment" as const, ...sendVideo }
                    : null,
                  externalVideoUrl: null,
                  editedAt: null,
                },
                sentAt: new Date().toISOString(),
              },
            })
          : undefined,
        update(cache, { data: mutationData }) {
          if (!mutationData?.addPrivateClassMessage) {
            return;
          }
          const newMessage = mutationData.addPrivateClassMessage;
          const targetThreadUid = threadUid ?? newMessage.thread_uid;
          cache.modify({
            id: cache.identify({
              __typename: "BlockScopedThread",
              uid: targetThreadUid,
            }),
            fields: {
              messages(existing = []) {
                const newRef = cache.writeFragment({
                  fragment: PrivateClassMessageFragment,
                  fragmentName: "PrivateClassMessageFragment",
                  data: newMessage,
                });
                return [...existing, newRef];
              },
              latestMessage(existing) {
                return (
                  cache.writeFragment({
                    fragment: PrivateClassMessageFragment,
                    fragmentName: "PrivateClassMessageFragment",
                    data: newMessage,
                  }) ?? existing
                );
              },
            },
          });
        },
      });

      onMessageSent?.();
      const newThreadUid = result.data?.addPrivateClassMessage?.thread_uid;
      if (!threadUid && newThreadUid && onThreadCreated) {
        onThreadCreated(newThreadUid);
      }
    } catch (err) {
      setText(trimmed);
      setAttachments(sendAttachments);
      setVideo(sendVideo);
      setError(err);
      OsPlatform.captureError(err);
    }
  };

  return (
    <Box
      sx={{
        borderTop: "1px solid",
        borderColor: "neutral.100",
        ...((sx ?? {}) as Record<string, unknown>),
      }}
    >
      <ErrorMessage value={error} sx={{ marginBottom: 0 }} />
      {(attachments.length > 0 || video) && (
        <Box sx={{ padding: "1.2rem 1.6rem 0" }}>
          <AttachmentGallery
            video={video}
            attachments={attachments}
            onDelete={handleDeleteAttachment}
            containerSx={{ marginBottom: 0 }}
            primaryAttachmentMaxHeight="120px"
          />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: "0.8rem",
          padding: "0.75rem 1.2rem",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <InlineEditableAttachments
            showVideoUpload={true}
            video={video}
            value={attachments}
            onChange={setAttachments}
            onVideoChanged={setVideo}
            sx={{ flexShrink: 0, color: "text.secondary" }}
          />
        </Box>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder={placeholder}
          value={text}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setText(e.target.value)
          }
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "20px",
            },
          }}
        />
        <IconButton
          icon={faPaperPlane}
          aria-label={t("Send message")}
          onClick={handleSend}
          disabled={!hasContent}
          sx={{
            backgroundColor: "primary.main",
            color: "primary.contrastText",
            "&:hover": { backgroundColor: "primary.dark" },
            "&.Mui-disabled": {
              backgroundColor: "action.disabledBackground",
              color: "action.disabled",
            },
            width: 40,
            height: 40,
            flexShrink: 0,
          }}
        />
      </Box>
    </Box>
  );
}
